import type {CouponData, CouponStatusValue} from '../types';
import type {PageProps} from '@router';

import {DeleteOutlined, PauseCircleOutlined} from '@ant-design/icons';
import {dateToReadableDate} from '@date-picker';
import {Button, Loading} from '@ds/components';
import {DetailPageLayout} from '@ds/layout';
import {toast} from '@Toast';
import {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'store';
import {getCouponDetails, pauseCoupon, removeCoupon} from 'store/slices/coupon/thunk';

import CouponInfoRow from '../components/common/CouponInfoRow/CouponInfoRow';
import CouponStatus from '../components/common/CouponStatus/CouponStatus';
import {COUPON_ROUTES} from '../constants';
import {getCouponVendorId} from '../utils';
import CouponActionModal from './components/CouponActionModal/CouponActionModal';

const CouponDetails: React.FC<PageProps> = ({headerData}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const vendorId = getCouponVendorId();
  const {couponId} = useParams();
  const {remove, pause} = useAppSelector((state) => state.coupon);
  const [details, setDetails] = useState<CouponData>();
  const [loadedCouponId, setLoadedCouponId] = useState<string>();
  const [actionStatus, setActionStatus] = useState<CouponStatusValue>();

  useEffect(() => {
    if (!couponId) {
      return;
    }

    let cancelled = false;
    setDetails(undefined);
    setLoadedCouponId(undefined);
    void dispatch(getCouponDetails({vendor_id: vendorId, coupon_id: Number(couponId)}))
      .unwrap()
      .then((response) => {
        if (!cancelled) {
          setDetails(response.data as CouponData);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDetails(undefined);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadedCouponId(couponId);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [couponId, dispatch, vendorId]);

  useEffect(() => {
    if (details?.status === 'draft') {
      headerData?.({
        component: (
          <Button
            color="destructive"
            size="sm"
            endIcon={<DeleteOutlined aria-hidden />}
            onClick={() => setActionStatus('draft')}
          >
            حذف
          </Button>
        )
      });
    } else if (details?.status === 'active') {
      headerData?.({
        component: (
          <Button
            color="destructive"
            size="sm"
            endIcon={<PauseCircleOutlined aria-hidden />}
            onClick={() => setActionStatus('active')}
          >
            توقف
          </Button>
        )
      });
    } else {
      headerData?.({
        icon: null,
        action: null,
        iconTitle: '',
        iconClassName: null,
        component: null
      });
    }
  }, [details?.status, headerData]);

  const runAction = async () => {
    if (!details || !actionStatus) {
      return;
    }

    try {
      if (actionStatus === 'draft') {
        await dispatch(removeCoupon({coupon_id: details.id, vendor_id: vendorId})).unwrap();
      } else {
        await dispatch(pauseCoupon({coupon_id: details.id, vendor_id: vendorId})).unwrap();
      }
      setActionStatus(undefined);
      navigate(COUPON_ROUTES.list);
    } catch {
      toast('مشکلی پیش آمده. دوباره تلاش کنید!');
    }
  };

  if (loadedCouponId !== couponId) {
    return (
      <div className="flex min-h-60 flex-1 items-center justify-center">
        <Loading size="large" color="primary" />
      </div>
    );
  }

  if (!details) {
    return (
      <div className="flex min-h-60 flex-1 items-center justify-center px-4 text-center">
        <p className="text-body-sm text-gray-08">اطلاعات کوپن برای نمایش در دسترس نیست.</p>
      </div>
    );
  }

  return (
    <>
      <DetailPageLayout contentClassName="px-4">
        <CouponInfoRow title="وضعیت" value={<CouponStatus value={details.status} />} />
        <CouponInfoRow
          title="تاریخ شروع"
          value={dateToReadableDate({
            date: details.start_time,
            day: true,
            month: true,
            year: true
          })}
        />
        <CouponInfoRow
          title="تاریخ پایان"
          value={dateToReadableDate({
            date: details.end_time,
            day: true,
            month: true,
            year: true
          })}
        />
        <CouponInfoRow title="شرط کوپن" value={details.description} />
        <CouponInfoRow title="هدیه کوپن" value={details.title} hasBorder={false} />
      </DetailPageLayout>
      <CouponActionModal
        status={actionStatus}
        loading={Boolean(remove.loading || pause.loading)}
        onClose={() => setActionStatus(undefined)}
        onConfirm={() => void runAction()}
      />
    </>
  );
};

export default CouponDetails;
